<script setup lang="ts">
import { ref } from "vue";

import AgentWebhookLink from "~/components/AgentWebhookLink.vue";
import Input from "~/components/dumb/Input.vue";
import Modal from "~/components/dumb/Modal.vue";
import { AGENT_CONFIG_MAP } from "~/constants/agents/configs";
import { ModalWidth } from "~/shared/enums";
import type { User } from "~/shared/types";
import { useDataStore } from "~/stores";
import { validateIsNotEmpty } from "~/utils/validation";

const props = defineProps<{
  agent: User | undefined;
}>();

const emit = defineEmits<{
  close: [];
}>();

const dataStore = useDataStore();

const inputRefs = ref<Record<string, InstanceType<typeof Input>>>({});
const setInputRef = (key: string, elem: InstanceType<typeof Input> | null) => {
  if (!elem) {
    delete inputRefs.value[key];
    return;
  }
  inputRefs.value[key] = elem;
};

const getConfig = (agent: User) => AGENT_CONFIG_MAP.get(agent.kind);

const updateField = (fieldKey: string, value: string) => {
  if (!props.agent || !inputRefs.value[fieldKey]?.isValid) {
    return;
  }
  dataStore.updateAgent({
    duid: props.agent.duid,
    agentConfig: {
      ...props.agent.agentConfig,
      [fieldKey]: value.trim(),
    },
  });
};
</script>

<template>
  <Modal
    :entity="agent"
    :title="(entity) => `Edit ${entity?.name ?? 'agent'}`"
    :width="ModalWidth.M"
    @close="emit('close')">
    <template #default="{ entity }">
      <div v-if="entity" class="my-4 flex flex-col gap-4">
        <div class="mb-4 select-none text-sm/relaxed text-lt">
          <!-- prettier-ignore -->
          <p>
            Learn more about how to set up the {{ entity.name }} agent from
            <a :href="getConfig(entity)?.helpUrl" target="_blank" rel="noopener noreferrer">
              <span class="underline text-vlt">this help center article</span>
            </a>.
          </p>
        </div>
        <template v-for="field in getConfig(entity)?.configFields" :key="field.key">
          <Input
            v-if="field.type !== 'webhook'"
            :ref="(elem) => setInputRef(field.key, elem as InstanceType<typeof Input>)"
            :init-value="entity.agentConfig[field.key]"
            :input-type="field.type"
            :placeholder="field.placeholder"
            :label="field.label"
            input-classes="!text-sm rounded-md"
            :validate="validateIsNotEmpty"
            @finalize="(value) => updateField(field.key, value)" />
          <AgentWebhookLink v-else :agent="entity" />
        </template>
      </div>
    </template>
  </Modal>
</template>
